import React from 'react'
import { Button, Dropdown, Menu, Tag } from 'antd'

import {
  buildSearchFilterProps,
  sortStringColumn,
  sortNumericColumn,
  sortBooleanColumn,
} from '../../../components/tables/utils'
import { MarketIndexTableItem, marketIndexTypes } from '../models'
import { MoreOutlined } from '@ant-design/icons'
import { renderCoupon, renderDate, renderTenantLink } from '../../../components/tables/table-column-renderers'

const renderActionsMenu =
  ({
    onDelete,
    onUpdate,
  }: {
    onDelete: (marketIndex: MarketIndexTableItem) => void
    onUpdate: (marketIndex: MarketIndexTableItem) => void
  }) =>
  (marketIndex: MarketIndexTableItem) => {
    const menu = (
      <Menu>
        <Menu.Item key="updateTenant" onClick={() => onUpdate(marketIndex)}>
          Update Market Index
        </Menu.Item>
        <Menu.Item key="deleteTenant" onClick={() => onDelete(marketIndex)}>
          Delete Market Index
        </Menu.Item>
      </Menu>
    )

    return (
      <Dropdown placement="bottomRight" overlay={menu}>
        <Button icon={<MoreOutlined />} />
      </Dropdown>
    )
  }

interface TableItemProps {
  onDelete: (marketIndex: MarketIndexTableItem) => void
  onUpdate: (marketIndex: MarketIndexTableItem) => void
}

export const buildTableColumns = ({ onDelete, onUpdate }: TableItemProps) => [
  {
    title: 'Id',
    dataIndex: 'id',
    sorter: sortNumericColumn({ dataIndex: 'id' }),
    ...buildSearchFilterProps({ dataIndex: 'id' }),
  },
  {
    title: 'Name',
    dataIndex: 'name',
    sorter: sortStringColumn({ dataIndex: 'name' }),
    ...buildSearchFilterProps({ dataIndex: 'name' }),
  },
  {
    title: 'Base Date',
    dataIndex: 'baseDate',
    sorter: sortStringColumn({ dataIndex: 'baseDate' }),
    render: (date) => <span>{renderDate(date)}</span>,
  },
  {
    title: 'Benchmark',
    dataIndex: 'isBenchmark',
    render: (isBenchmark) => (
      <Tag color={isBenchmark ? 'green' : 'red'} className="capitalize">
        {isBenchmark ? 'yes' : 'no'}
      </Tag>
    ),
    sorter: sortBooleanColumn({ dataIndex: 'isBenchmark' }),
  },
  {
    title: 'Type',
    dataIndex: 'type',
    filters: marketIndexTypes.map(({ label, value }) => ({ text: label, value })),
    onFilter: (type: string, record: MarketIndexTableItem) => record.type.indexOf(type) === 0,
    render: (type) => <Tag>{type}</Tag>,
    sorter: sortBooleanColumn({ dataIndex: 'type' }),
  },
  {
    title: 'Tenant',
    dataIndex: 'tenantDomain',
    sorter: sortNumericColumn({ dataIndex: 'tenantDomain' }),
    ...buildSearchFilterProps({
      dataIndex: 'tenantDomain',
      render: (domain) => (domain ? renderTenantLink(domain) : '-'),
    }),
  },
  {
    title: '1M',
    dataIndex: '1M',
    sorter: sortNumericColumn({ dataIndex: '1M' }),
    render: (coupon) => <span>{renderCoupon(coupon)}</span>,
  },
  {
    title: '3M',
    dataIndex: '3M',
    sorter: sortNumericColumn({ dataIndex: '3M' }),
    render: (coupon) => <span>{renderCoupon(coupon)}</span>,
  },
  {
    title: '6M',
    dataIndex: '6M',
    sorter: sortNumericColumn({ dataIndex: '6M' }),
    render: (coupon) => <span>{renderCoupon(coupon)}</span>,
  },
  {
    title: '12M',
    dataIndex: '12M',
    sorter: sortNumericColumn({ dataIndex: '12M' }),
    render: (coupon) => <span>{renderCoupon(coupon)}</span>,
  },
  {
    title: 'YTD',
    dataIndex: 'YTD',
    sorter: sortNumericColumn({ dataIndex: 'YTD' }),
    render: (coupon) => <span>{renderCoupon(coupon)}</span>,
  },
  { title: '', render: renderActionsMenu({ onDelete, onUpdate }) },
]
